import { z } from "zod";
import type { DbtCauseView, DbtMaterialization, DbtResultStatus, DbtTestKind } from "./dbtCauses";
import type { ImportCauseAction, ImportCauseView } from "./importCauses";
import type { PermissionKey } from "../permissions";
import { localDateOf, startOfLocalDate } from "../cron/cronUtils";
import { displayTimeZoneSchema } from "./userPreferences";

/**
 * Run request and response contracts (FR-52, FR-54, FR-56, API-STANDARD.md §5).
 *
 * A run id is NOT a uuid. It is the composite `{hex8}-{yyyymmddhhmmss}-{hex8}` the runner
 * mints (DATA-MODEL.md §3), matching the donor DAG's `correlationId` shape, so every route
 * that names one validates with `runIdSchema` rather than `z.uuid()`.
 *
 * One consequence worth stating because it shapes the list route: the timestamp sits in the
 * MIDDLE of a run id, behind eight random hex characters. Ordering by `run_id` is therefore
 * not chronological, and the history cursor is `(created_at, run_id)`, never the id alone.
 */

export const RUN_STATUSES = [
  "queued",
  "running",
  "succeeded",
  "failed",
  "partially_succeeded",
  "cancelled",
] as const;
export type RunStatus = (typeof RUN_STATUSES)[number];

export const RUN_KINDS = ["scheduled", "on_demand", "retry", "event_drain"] as const;
export type RunKind = (typeof RUN_KINDS)[number];

/**
 * What a run did, for the runs list (Session 10, B13). Derived, never stored: `import` has no
 * `transform` step, `transform` has one and no connection steps, `import_transform` has both.
 */
export const RUN_TYPES = ["import", "transform", "import_transform"] as const;
export type RunType = (typeof RUN_TYPES)[number];

export const STEP_NAMES = ["extract", "load", "merge", "transform", "finalize"] as const;
export type StepName = (typeof STEP_NAMES)[number];

export const STEP_STATUSES = [
  "pending",
  "running",
  "succeeded",
  "failed",
  "skipped",
  "cancelled",
] as const;
export type StepStatus = (typeof STEP_STATUSES)[number];

export const ENTITY_PROGRESS_STATUSES = [
  "pending",
  "running",
  "succeeded",
  "failed",
  "skipped",
] as const;
export type EntityProgressStatus = (typeof ENTITY_PROGRESS_STATUSES)[number];

/**
 * #1127: a run detail table's status. `waiting` is never stored: the server adds it for a table
 * the extract's current attempt set out to read (`run_steps.metrics.entityNames`) that has not
 * reported yet, so the total is the planned tables from the first moment. A waiting row has no
 * rows, is not a failure, and no total counts it; once the run is over it reads Not extracted.
 */
export type RunEntityStatus = EntityProgressStatus | "waiting";

/** `{hex8}-{yyyymmddhhmmss}-{hex8}`; the same expression `runner/scheduler/runFactory.ts` holds. */
export const RUN_ID_REGEX = /^[0-9a-f]{8}-\d{14}-[0-9a-f]{8}$/;

export const runIdSchema = z
  .string()
  .regex(RUN_ID_REGEX, "A run id looks like 0db0c32a-20260919024128-70df4fe9.");

export const runIdParamsSchema = z.object({ runId: runIdSchema });

/**
 * How one table of a scoped run is read (product gaps row 2, design section 4.1): `incremental`
 * from the table's own watermark, `full` every row again (a re-sync of that table only), `since`
 * the changes since a date (a backfill), which never moves the table's watermark backwards.
 */
export const RUN_TABLE_MODES = ["incremental", "full", "since"] as const;
export type RunTableMode = (typeof RUN_TABLE_MODES)[number];

/** The most tables one scoped run or one table retry names. */
export const MAX_RUN_TABLES = 500;

/** The earliest `since` date a backfill may name. */
export const MIN_SINCE_DATE = "1900-01-01";

/**
 * A `since` date (`YYYY-MM-DD`) as the instant it means: the first instant of that day in
 * `timeZone` (#1078: the screen sends the reader's display zone, so "Oct 14" is midnight of Oct 14
 * where they are), else midnight UTC for an API caller that names no zone.
 */
export function sinceInstant(date: string, timeZone?: string): Date {
  if (timeZone) return startOfLocalDate(date, timeZone);
  return new Date(`${date}T00:00:00.000Z`);
}

/** Table names as a run names them: `dataflow_entities.entity_name`, 1 to 500 of them. */
const entityNamesSchema = z
  .array(z.string().trim().min(1).max(400))
  .min(1, "Name at least one table.")
  .max(MAX_RUN_TABLES, `Name at most ${MAX_RUN_TABLES} tables.`);

/** The table set of a Run now for one connection. */
export const runTablesSchema = z.object({
  mode: z.enum(RUN_TABLE_MODES),
  /** `YYYY-MM-DD`, only with `mode: "since"`; read as midnight in `sinceTimeZone` (`sinceInstant`). */
  since: z.iso.date().optional(),
  /**
   * #1078: the IANA zone whose midnight starts `since` (the screen sends the reader's display
   * zone). Absent, the day starts at midnight UTC, as before, for an API caller.
   */
  sinceTimeZone: displayTimeZoneSchema.optional(),
  entityNames: entityNamesSchema,
});
export type RunTables = z.infer<typeof runTablesSchema>;

/**
 * UTC+14, the easternmost offset: a date is "today or earlier" for some reader while its midnight
 * UTC is at most this far ahead of now (review r1, N2). The client offers the reader's own date.
 */
const EASTERNMOST_OFFSET_MS = 14 * 3_600_000;

/**
 * What is wrong with a scoped run's `since`, if anything: mode "since" needs a date from
 * `MIN_SINCE_DATE` to today (in `sinceTimeZone` when named, else anywhere on Earth), and no other
 * mode takes one.
 */
function tablesSinceIssue(tables: z.infer<typeof runTablesSchema>): string | undefined {
  if (tables.mode !== "since") {
    return tables.since === undefined && tables.sinceTimeZone === undefined
      ? undefined
      : 'A date goes with mode "since" only.';
  }
  if (!tables.since) return 'Mode "since" needs a date.';
  if (tables.since < MIN_SINCE_DATE) return `The date is on or after ${MIN_SINCE_DATE}.`;
  // With a zone the date is exact: today or earlier in that zone (#1078 review). Without one,
  // midnight UTC with the easternmost allowance, as before.
  const future = tables.sinceTimeZone
    ? tables.since > localDateOf(new Date(), tables.sinceTimeZone)
    : sinceInstant(tables.since).getTime() > Date.now() + EASTERNMOST_OFFSET_MS;
  return future ? "The date is today or earlier." : undefined;
}

/**
 * FR-52: a schedule or a single connection, never both and never neither.
 *
 * The two are the same run downstream — `createRunWithSteps` builds identical rows — so the
 * difference lives only here, in which set of connections the run covers.
 *
 * Product gaps row 2: `tables` scopes a run of ONE connection to some of its selected tables and
 * says how each is read. A schedule run is never scoped (owner answer of 2026-09-28), and
 * `fullRefresh` is the run-level form of `tables.mode: "full"`, so the two never travel together.
 */
export const startRunBodySchema = z
  .object({
    scheduleId: z.uuid().optional(),
    dataflowId: z.uuid().optional(),
    /**
     * Session 10 (P7, B3): import every row again instead of only what changed since the last
     * import. Stored on the run row and kept by every retry. Harmless on a full connection.
     */
    fullRefresh: z.boolean().default(false),
    /**
     * Round 4, item 7: with a `scheduleId` only, the connections of that schedule to import in
     * full; the others use their own refresh type. Each must be one of the schedule's connections.
     * Not with `fullRefresh: true`, which already names every connection.
     */
    fullRefreshDataflowIds: z.array(z.uuid()).max(200).optional(),
    tables: runTablesSchema.optional(),
  })
  .superRefine((value, ctx) => {
    const hasSchedule = Boolean(value.scheduleId);
    const hasDataflow = Boolean(value.dataflowId);
    if (hasSchedule === hasDataflow) {
      ctx.addIssue({
        code: "custom",
        path: ["dataflowId"],
        message: hasSchedule
          ? "Send either scheduleId or dataflowId, not both."
          : "A run needs a scheduleId or a dataflowId.",
      });
    }
    if (value.fullRefreshDataflowIds !== undefined) {
      if (!hasSchedule || hasDataflow) {
        ctx.addIssue({
          code: "custom",
          path: ["fullRefreshDataflowIds"],
          message:
            "Connections to import in full are chosen for a schedule run. Send fullRefresh for one connection.",
        });
      } else if (value.fullRefresh) {
        ctx.addIssue({
          code: "custom",
          path: ["fullRefreshDataflowIds"],
          message: "Send fullRefresh or fullRefreshDataflowIds, not both.",
        });
      }
    }
    const tables = value.tables;
    if (!tables) return;
    if (!hasDataflow || hasSchedule) {
      ctx.addIssue({
        code: "custom",
        path: ["tables"],
        message: "Tables can be chosen for one connection only. A schedule run covers every table.",
      });
    }
    if (value.fullRefresh) {
      ctx.addIssue({
        code: "custom",
        path: ["fullRefresh"],
        message:
          'Send fullRefresh or tables, not both. With tables.mode "full", those tables are read again in full.',
      });
    }
    const sinceIssue = tablesSinceIssue(tables);
    if (sinceIssue) {
      ctx.addIssue({ code: "custom", path: ["tables", "since"], message: sinceIssue });
    }
  });
/** What a caller sends: `fullRefresh` may be left out (the parsed body always carries it). */
export type StartRunBody = z.input<typeof startRunBodySchema>;

/**
 * FR-54: re-enqueue exactly one step. `dataflowId` is omitted for the schedule-level
 * `transform` step and the run-level `finalize` step, which is what the `NULLS NOT DISTINCT`
 * identity key on `run_steps` was built for.
 */
export const retryRunBodySchema = z.object({
  step: z.enum(STEP_NAMES),
  dataflowId: z.uuid().optional(),
});
export type RetryRunBody = z.infer<typeof retryRunBodySchema>;

/**
 * Product gaps row 2: `POST /runs/:runId/retry-tables`. A NEW run of one connection of that run,
 * covering the named tables, or, with no names, every table that did not land in it (its extract
 * failed or skipped it, or its load failed). A table whose LOAD failed is loaded again from the
 * files that run staged, with no new extract (owner answer of 2026-09-28).
 */
export const retryTablesBodySchema = z.object({
  dataflowId: z.uuid(),
  entityNames: entityNamesSchema.optional(),
});
export type RetryTablesBody = z.infer<typeof retryTablesBodySchema>;

/**
 * FR-54: the reason is required and audited. It is `optional()` here so the route can answer
 * 422 ("a valid request that cannot be applied") rather than 400, which is the status the API
 * standard reserves for a shape failure; a force reset with no reason is a well-formed request
 * this system refuses to perform.
 */
export const FORCE_RESET_REASON_MIN_LENGTH = 10;

export const forceResetBodySchema = z.object({
  // B-40: the floor the dialog shows is the floor the server holds (400 below it, after trim).
  reason: z
    .string()
    .trim()
    .min(FORCE_RESET_REASON_MIN_LENGTH, {
      error: `Say why, in at least ${FORCE_RESET_REASON_MIN_LENGTH} characters.`,
    })
    .max(500)
    .optional(),
});
export type ForceResetBody = z.infer<typeof forceResetBodySchema>;

/** ISO-8601 instants, so a caller can hand back exactly what a previous response gave it. */
const isoDate = z.iso.datetime({ offset: true });

/**
 * What `include` may add to each row of `GET /runs` (Session 11, D4): `connections` is every
 * connection's part of each run (`RunSummaryRow.connections`), the transform step's facts and the
 * overlap flag; it needs `scheduleId` (a schedule's History reads it) and caps `limit`.
 */
export const RUNS_INCLUDES = ["connections"] as const;
export type RunsInclude = (typeof RUNS_INCLUDES)[number];

/** The largest page `include=connections` may ask for: each row then carries every step array. */
export const RUNS_INCLUDE_MAX_LIMIT = 50;

/**
 * Round 10 wave 2: the Runs list's status filter takes one status or several, comma separated
 * (`?status=queued,running`, Overview's "Running now"). Each must be a run status; repeats fold
 * into one, in `RUN_STATUSES` order, so one set of statuses is one query string.
 */
export const runStatusListSchema = z
  .string()
  .max(200)
  .transform((value) => value.split(",").map((part) => part.trim()))
  .pipe(z.array(z.enum(RUN_STATUSES)).min(1))
  .transform((statuses) => RUN_STATUSES.filter((status) => statuses.includes(status)));

/** The status filter written back to a query string: `queued,running`. */
export function runStatusListParam(statuses: readonly RunStatus[]): string {
  return RUN_STATUSES.filter((status) => statuses.includes(status)).join(",");
}

export const runsQuerySchema = z
  .object({
    cursor: z.string().max(512).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    status: runStatusListSchema.optional(),
    kind: z.enum(RUN_KINDS).optional(),
    /** Session 11 (D8): what the run did, the same derivation as `RunSummaryRow.type`. */
    type: z.enum(RUN_TYPES).optional(),
    dataflowId: z.uuid().optional(),
    scheduleId: z.uuid().optional(),
    /**
     * Round 10 (O1, O2): only the runs the caller started. The one value is `me`; the route
     * reads the caller's id from the session, so a client never sends (and cannot forge) one.
     */
    startedBy: z.enum(["me"]).optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    include: z.enum(RUNS_INCLUDES).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.include === undefined) return;
    if (!value.scheduleId) {
      ctx.addIssue({
        code: "custom",
        path: ["include"],
        message: "include=connections needs scheduleId.",
      });
    }
    if (value.limit > RUNS_INCLUDE_MAX_LIMIT) {
      ctx.addIssue({
        code: "custom",
        path: ["limit"],
        message: `limit is at most ${RUNS_INCLUDE_MAX_LIMIT} with include=connections.`,
      });
    }
  });
export type RunsQuery = z.infer<typeof runsQuerySchema>;

/**
 * The two orders of a run's event log (Session 10, P11): newest first (the default, what a
 * reader of a live run wants) or oldest first (what a reader of a finished run reads top-down).
 */
export const RUN_EVENT_ORDERS = ["newest", "oldest"] as const;
export type RunEventOrder = (typeof RUN_EVENT_ORDERS)[number];

/**
 * An event code as the `event` filter takes it. Connector names are stored verbatim
 * (`progressDrain.ts`), so digits are allowed; anything else (spaces, quotes, lower case) is a
 * 400, never a query.
 */
export const RUN_EVENT_CODE_REGEX = /^[A-Z0-9_]{1,64}$/;

/** The most codes one `event` filter names. */
export const MAX_RUN_EVENT_CODES = 20;

/**
 * Which step an event belongs to when it carries no `payload.step` (Session 10, P11). There is
 * no step column on `run_events`: a step is implied by the event's prefix. `STEP_*`,
 * `STEP_RETRY_REQUESTED` and `TRANSFORM_SKIPPED` carry `payload.step` and are matched by it;
 * merge and finalize write nothing of their own, so they are matched by the payload only.
 *
 * Codes no prefix covers, and which belong to no step unless their payload names one:
 * `RUN_CREATED`, `RUN_RUNNING`, `RUN_FINISHED`, `RUN_CANCELLED`, `RUN_FORCE_RESET`, `RUN_QUEUED_CAPACITY`,
 * `RUN_ADMITTED_CAPACITY`, `RUN_QUEUED_LOAD`, `RUN_ADMITTED_LOAD` (#1651, a load's push slot; both carry `step: load`),
 * `RUN_QUEUED_TRANSFORM`, `RUN_ADMITTED_TRANSFORM` (round 6, the dbt slot),
 * `SCHEDULE_SKIPPED_OVERLAP`, `EXECUTION_ORPHAN_STOPPED`,
 * `PROGRESS_DEAD_LETTER`.
 *
 * Connector codes are stored verbatim by the progress drain, so a connector event outside the
 * `TABLE_` and `EXTRACT_` families that carries no `payload.step` belongs to no step either:
 * `step=extract` does not list it, and only the unfiltered log (or its own `event` code) shows it.
 */
export const STEP_EVENT_PREFIXES: Readonly<Record<StepName, readonly string[]>> = {
  extract: ["TABLE_", "EXTRACT_"],
  load: ["LOAD_"],
  merge: [],
  transform: ["TRANSFORM_", "DBT_"],
  finalize: [],
};

/** A query value as a list: a repeated key is already one, a single value becomes one. */
function asQueryList(value: unknown): unknown {
  if (value === undefined) return undefined;
  return Array.isArray(value) ? value : [value];
}

/** One value or a repeated query key, as Express hands either to the schema. */
const eventCodesSchema = z.preprocess(
  asQueryList,
  z
    .array(z.string().regex(RUN_EVENT_CODE_REGEX, "An event code looks like STEP_SUCCEEDED."))
    .min(1)
    .max(MAX_RUN_EVENT_CODES, `Name at most ${MAX_RUN_EVENT_CODES} event codes.`)
    .optional(),
);

export const runEventsQuerySchema = z.object({
  cursor: z.string().max(512).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  level: z.enum(["info", "warn", "error"]).optional(),
  dataflowId: z.uuid().optional(),
  entityName: z.string().max(400).optional(),
  /** Session 10 (P11): newest first unless asked; the cursor carries the order it was made in. */
  order: z.enum(RUN_EVENT_ORDERS).default("newest"),
  /** Session 10 (P11): the step's own events, by `payload.step` or `STEP_EVENT_PREFIXES`. */
  step: z.enum(STEP_NAMES).optional(),
  /** Session 10 (P11): only these event codes (repeat the key for several). */
  event: eventCodesSchema,
});
export type RunEventsQuery = z.infer<typeof runEventsQuerySchema>;

/** The most events one export returns; a longer log is cut and says so (`truncated`). */
export const MAX_EVENT_EXPORT_ROWS = 10_000;

/**
 * The event export's filters (Session 10, B13): every events filter except the page controls.
 * Derived from `runEventsQuerySchema`, so a filter added there reaches the export unchanged.
 * `order` is a page control too (Session 10 review): the export is always oldest first, so the key
 * is not accepted, and like any unknown query key it is stripped (API-STANDARD: query schemas
 * strip; only a body that is strict on purpose refuses).
 */
export const runEventsExportQuerySchema = runEventsQuerySchema
  .omit({
    cursor: true,
    limit: true,
    order: true,
  })
  .extend({
    /**
     * #1078: the reader's display zone. The file's times are then ISO 8601 with that zone's offset
     * (`2026-10-14T12:42:00.000+05:30`, `shared/time/isoInZone.ts`) and the text log names the zone.
     * Absent, an API caller gets UTC (`Z`), as before. Not a filter: the rows are the same.
     */
    tz: displayTimeZoneSchema.optional(),
  });
export type RunEventsExportQuery = z.infer<typeof runEventsExportQuerySchema>;

/**
 * The download formats of `GET /runs/:runId/events/export.{csv,jsonl,txt}`. Round 10 (R2.3): `txt`
 * is the readable log, one line per event, with the run's header lines.
 */
export const RUN_EVENT_EXPORT_FORMATS = ["csv", "jsonl", "txt"] as const;
export type RunEventExportFormat = (typeof RUN_EVENT_EXPORT_FORMATS)[number];

/** The export's columns, in order; the JSON Lines objects carry the same keys. */
export const RUN_EVENT_EXPORT_COLUMNS = [
  "at",
  "level",
  "event",
  "connection",
  "entity",
  "payload",
] as const;

/**
 * #1673: why a schedule's Run now left a connection out, closed. `disabled`: turned off by hand.
 * `untested`: its target changed since its last connection test (`TARGET_CHANGED`). `not_ready`:
 * any other Draft. `missing`: the schedule names an id with no connection (deleted). The same
 * rules as the due scan's `leftOutConnectionsOf`, which records them as `schedule.skip` rows.
 */
export const RUN_LEFT_OUT_REASONS = ["disabled", "untested", "not_ready", "missing"] as const;
export type RunLeftOutReason = (typeof RUN_LEFT_OUT_REASONS)[number];

/** #1673: one connection a schedule's Run now left out, ids and a closed code only (SEC-17). */
export interface RunLeftOut {
  dataflowId: string;
  reason: RunLeftOutReason;
}

const LEFT_OUT_REASONS: ReadonlySet<string> = new Set(RUN_LEFT_OUT_REASONS);

/**
 * #1673: the well formed entries of a stored `leftOut` (a `RUN_CREATED` payload's), anything else
 * dropped. The run page, the event sentence and a replayed start's 202 all read it this way.
 */
export function parseRunLeftOut(value: unknown): RunLeftOut[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry: unknown) => {
    if (entry === null || typeof entry !== "object") return [];
    const { dataflowId, reason } = entry as Record<string, unknown>;
    if (typeof dataflowId !== "string" || typeof reason !== "string") return [];
    if (!LEFT_OUT_REASONS.has(reason)) return [];
    return [{ dataflowId, reason: reason as RunLeftOutReason }];
  });
}

/** The 202 body of `POST /runs` (API-STANDARD.md §2: the run id and a status URL). */
export interface StartRunResponse {
  runId: string;
  status: RunStatus;
  kind: RunKind;
  statusUrl: string;
  /** Connections this run covers, in the order their step rows were inserted. */
  dataflowIds: string[];
  /** False when the queue publish failed; the reconcile pass owns the run in that case. */
  published: boolean;
  /**
   * Session 10 (P7): whether this run is a full refresh, as stored on its row (a replay answers
   * the stored value). Absent on the transform-only start, which has nothing to refresh.
   * Round 4, item 7: true when every incremental connection of the run imports in full.
   */
  fullRefresh?: boolean;
  /**
   * Round 4, item 7: the connections this run imports in full, as stored on its row (a replay
   * answers the stored set). Absent where `fullRefresh` is.
   */
  fullRefreshDataflowIds?: string[];
  /**
   * #1673: the schedule's connections this run left out and why, in the schedule's order. Only on
   * a schedule's start that left one out, as on its `RUN_CREATED` event; a replay of the key
   * answers the stored list.
   */
  leftOut?: RunLeftOut[];
}

/** The 202 body of `POST /runs/:runId/retry-tables`: the new run, and what it covers. */
export interface RetryTablesResult extends StartRunResponse {
  /** The run the tables came from. */
  retryOf: string;
  dataflowId: string;
  /** Tables the new run covers. */
  tableCount: number;
  /** Of those, the tables loaded from the source run's staged files with no new extract. */
  fromStagedFiles: number;
}

/**
 * Which step a table did not land in, and how a table retry reads it again (product gaps row 2).
 * `load` with `retryFrom: "staged"` reloads the files the run staged; everything else extracts
 * the table again.
 */
export interface RetryableTable {
  entityName: string;
  failedStep: "extract" | "load";
  /** `skipped`: the extractor reported skipping it (a partial extract), which is also a miss. */
  outcome: "failed" | "skipped";
  retryFrom: "staged" | "extract";
  /**
   * How the retry reads it: the mode it had in the run (a failed re-sync is re-synced, a forced
   * full run's table is read whole), and the `since` instant for a backfill, else null.
   */
  mode: RunTableMode;
  since: string | null;
}

/** One connection's tables that did not land in a run, for the retry picker. */
export interface RetryableConnection {
  dataflowId: string;
  tables: RetryableTable[];
}

/**
 * One connection's part of a scoped run (product gaps row 2): how many tables it covers and how
 * they are read. `mode` is `mixed` when a table retry inherited more than one mode. Counts and a
 * date only (SEC-17); the names are on the run's steps and tables.
 */
export interface RunScopeSlice {
  dataflowId: string;
  mode: RunTableMode | "mixed";
  /** The `since` instant when every table of the slice reads from the same one; else null. */
  since: string | null;
  tableCount: number;
  /** Tables loaded from another run's staged files with no new extract. */
  fromStagedFiles: number;
}

/** The 202 body of `POST /runs/:runId/retry`: one type for the server that writes it and the client. */
export interface RetryRunResult {
  runId: string;
  step: StepName;
  dataflowId: string | null;
  attempt: number;
  /**
   * The later steps of the chain re-opened alongside it. `finalize` is always among them,
   * except when it IS the retried step; the schedule-level `transform` joins when the run
   * carries a terminal one and the retry can change what landed (so: not a `finalize` retry).
   */
  reopened: string[];
  /** True when the `Idempotency-Key` matched an earlier retry of this run: nothing was written. */
  replayed: boolean;
  /** Whether the step's job reached the queue; null on a replay, which enqueues nothing. */
  published: boolean | null;
}

/**
 * Who or what started a run, resolved at read time (Session 11, D3). `label` is a display name, a
 * masked address (never a full one) or "Removed user" for `user`; "Scheduler · {schedule}" for
 * `scheduler`; "System" for anything else (older rows, migrations, seeds). In a display name and
 * in the schedule's name of a scheduler label, digit runs of four or more read `#` and every
 * address is masked. The raw `triggeredBy` stays on the row for older clients.
 *
 * Round 4 (owner decision 10b, 2026-09-30): `name` is the specific half alone, masked the same
 * way: the person's label ("Ada Lovelace", "a***@example.com", "Removed user") for `user`, the
 * schedule's name (or "a deleted schedule") for `scheduler`, "System" for `system`. A screen
 * shows the kind as a word and `name` under it, and never slices `label` to get there. Absent
 * only from a server before round 4.
 */
export interface RunStartedBy {
  kind: "user" | "scheduler" | "system";
  label: string;
  name?: string;
}

/** A transform step's own facts on a schedule's History (Session 11, P3), for its one row. */
export interface RunTransformStepFacts {
  status: StepStatus;
  attempt: number;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
}

export interface RunSummaryRow {
  runId: string;
  scheduleId: string | null;
  kind: RunKind;
  status: RunStatus;
  /** The raw actor: a platform user id, `skydata-runner`, or a service token. Kept for older clients. */
  triggeredBy: string | null;
  /**
   * Session 11 (D3): who started the run. Absent only when the users read failed for the page
   * (the page still answers), and on rows from older servers.
   */
  startedBy?: RunStartedBy;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
  /** Step counts by status, cheap enough to compute for a list page. */
  stepCounts: Record<StepStatus, number>;
  dataflowIds: string[];
  /**
   * #817: the connections of `dataflowIds` with a failed step (extract or load), the same rule
   * Connections reads a connection Failed by, so a partly failed run names them ("Partly failed:
   * 1 of 2 connections failed (HR)", `shared/runs/partlyFailed.ts`). Absent when none failed,
   * and on rows from older servers.
   */
  failedDataflowIds?: string[];
  /**
   * #1483: the part of `failedDataflowIds` whose load landed at least one table (a load entity
   * with status `succeeded`), so the run reads "partly loaded" for them, not "failed". Absent when
   * none, and on rows from older servers.
   */
  partlyLoadedDataflowIds?: string[];
  /** #817: true when the run's transform step failed; absent otherwise, and on older servers. */
  transformFailed?: boolean;
  /**
   * Rows per run for the one-line summary (Session 7, O1). Absent for a run with no tables (a
   * transform-only run) and on rows from older servers.
   */
  totals?: RunTotals;
  /** What the run did (Session 10). Absent only on rows from older servers. */
  type?: RunType;
  /**
   * The transformations of the run's `transform` step, in the order the step recorded them
   * (`metrics.ranDbtJobIds`, else the pinned `metrics.dbtJobIds`). An id whose
   * transformation no longer exists reads "Deleted dbt job". Present only when the run
   * has a transform step; empty until the step recorded which ones it ran.
   */
  dbtJobNames?: string[];
  /** The transform step's model and test counts; present only when the run has a transform step. */
  transformTotals?: RunTransformTotals;
  /**
   * Round 6, T1: while the run's transform waits for the instance's one dbt slot, the run it waits
   * for ("Queued: waiting for run {id}"). Absent otherwise.
   */
  transformQueuedBehind?: string;
  /**
   * W6: the transform step's own duration in whole seconds, once it started and finished, so a
   * screen that shows the run's `durationSeconds` too labels them "Run" and "dbt step". Absent
   * otherwise.
   */
  transformDurationSeconds?: number;
  /** This connection's part of the run; only when the list query names `dataflowId`. */
  connection?: RunConnectionSlice;
  /**
   * Session 11 (P3, D4): every connection's part of the run, in the schedule's order; only with
   * `include=connections`.
   */
  connections?: RunConnectionSlice[];
  /** Session 11 (P3): the run-level transform step's facts; only with `include=connections`. */
  transformStep?: RunTransformStepFacts;
  /**
   * Session 11 (P3): a later firing was skipped because this run was still going (a
   * `SCHEDULE_SKIPPED_OVERLAP` event names it); only with `include=connections`.
   */
  overlapSkipped?: boolean;
  /**
   * Session 10 (P7, B3): the run imports every row again. The list and the run page show a "Full
   * refresh" badge only when this is true; absent on older servers. Round 4, item 7: true when
   * every incremental connection of the run imports in full. Round 7 (instance 0018): derived from
   * `fullRefreshDataflowIds` (`runForcesEveryConnectionSql`); no column stores it.
   */
  fullRefresh?: boolean;
  /**
   * Round 4, item 7: the connections this run imports in full (`runs.full_refresh_dataflow_ids`;
   * instance 0018 folded a run an older server forced with `full_refresh` alone into it). Present where
   * `fullRefresh` is; absent on older servers.
   */
  fullRefreshDataflowIds?: string[];
  /** Product gaps row 2: the run a table retry came from; null or absent otherwise. */
  retryOf?: string | null;
  /**
   * Product gaps row 2: the connections this run covers only in part. Absent (or empty) when the
   * run covers every selected table of every connection, as every run before this did.
   */
  scope?: RunScopeSlice[];
  /**
   * #1200: on a failed or partly failed run, another queued or running run that holds one of its
   * connections (the oldest). A retry is refused while it does (409 `RUN_IN_PROGRESS`), so the
   * Runs list and the run page show Retry from failed step off with the reason. Absent otherwise,
   * and on older servers.
   */
  heldByRunId?: string;
}

/**
 * The transform step's counts (Session 10), from its `run_steps.metrics` for the current
 * attempt (a retry drops them, `RETRY_ATTEMPT_METRICS`). Null means "not reported", never zero.
 */
export interface RunTransformTotals {
  modelsReported: number | null;
  modelsFailed: number | null;
  /**
   * Round 6, T5: present only when the step recorded them (a runner from round 6 on); with
   * `modelsFailed` they add up to `modelsReported`. Built is `modelsSucceeded`.
   */
  modelsSucceeded?: number | null;
  modelsSkipped?: number | null;
  modelsNotRun?: number | null;
  testsTotal: number | null;
  testsSucceeded: number | null;
  testsFailed: number | null;
}

/** One step of one connection in a run, as the connection's Runs tab lists it. */
export interface RunConnectionStep {
  step: StepName;
  status: StepStatus;
  attempt: number;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
}

/**
 * One connection's part of a run (Session 10, B17), on `GET /runs?dataflowId=`.
 *
 * `steps` are this connection's own step rows in chain order; the run-level `transform` and
 * `finalize` rows (null connection) are not among them. `durationSeconds` is from the first of
 * those steps to start to the last to finish, null while any of them is still pending or
 * running. `totals` are this connection's tables only, under the `RunTotals` rules, so the
 * slices of a run add up to its own totals; absent when the run has no table for it.
 */
export interface RunConnectionSlice {
  dataflowId: string;
  steps: RunConnectionStep[];
  durationSeconds: number | null;
  totals?: RunTotals;
}

/**
 * Per-run totals behind the one-line summary on the runs list, the run detail and the
 * connection's last-run card (Session 7, O1).
 *
 * Built from the full outer merge of `entity_progress` (the extract) and the load step's
 * `metrics.entities` (the load), so a table the drain never heard about still counts.
 * `dataCompleteness` is deliberately absent: its meaning is unclear (a table can report 97.14
 * with every row loaded), and a number nobody can explain is worse than none.
 */
export interface RunTotals {
  /** Tables in the merge of `entity_progress` and the load metrics. */
  tables: number;
  /** Tables whose extract failed OR whose load failed; a table counts once. */
  tablesFailed: number;
  /** Tables whose load failed. */
  tablesLoadFailed: number;
  /**
   * Sum of `entity_progress.records`. An extract retry does not clear those rows, so this is
   * the highest count any attempt reported per table.
   */
  rowsExtracted: number;
  /** Sum of the COPY counts; null when no load reported one (no load yet, or nothing loaded). */
  rowsLoaded: number | null;
  /** Sum of the extract manifest's counts, as the load read them; null when no load reported one. */
  expectedRows: number | null;
}

export interface RunStepRow {
  id: string;
  dataflowId: string | null;
  dataflowName: string | null;
  step: StepName;
  status: StepStatus;
  attempt: number;
  executionName: string | null;
  externalId: string | null;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
  metrics: unknown;
  /**
   * Product gaps row 4: a failed or partial extract's or load's classified cause, the server's
   * words for `metrics.importCause` (`server/services/importCauseView.ts`). Null when the step
   * recorded none (an older run, a step that succeeded cleanly); absent on a row not from
   * `GET /runs/:runId`.
   */
  cause?: ImportCauseView | null;
  /**
   * #1651: true on a `pending` load step whose push waits for a slot of the instance's import
   * limit on its current attempt (`metrics.pushQueuedAttempt`), so the run page says the load
   * waits instead of reading Loading. Absent otherwise, and on older servers.
   */
  waitingForSlot?: true;
  /**
   * #1672: on a `running` load step whose push is sending the tables' files to Snowflake now,
   * when it began (the push's `PUSH_STARTED` of the current attempt with no end after it), so the
   * run page says so instead of "0 of 40 tables". Absent otherwise, and on older servers.
   */
  sendingFilesSince?: string;
  /**
   * #2389: on a `running` extract step of a connection, the newest sign that it is moving: the
   * step's start or its connection's newest progress message (`TABLE_*`, the periodic
   * `TABLE_PROGRESS` among them, or `EXTRACT_*`), whichever is later
   * (`server/services/extractActivity.ts`). The run page reads it for "Last activity". Absent
   * on every other step and from an older server.
   */
  lastActivityAt?: string;
  /**
   * #2389: on the same step, the newest activity the connector itself reported (a progress
   * message), absent while it has reported none (the job may still be starting, or never start).
   */
  connectorActivityAt?: string;
}

export const ENTITY_LOAD_STATUSES = ["succeeded", "skipped", "failed"] as const;
export type EntityLoadStatus = (typeof ENTITY_LOAD_STATUSES)[number];

/**
 * The load step's outcome for one table (Session 7, O1): `run_steps.metrics.entities[]` of the
 * load step, plus the `LOAD_*` events of the current attempt.
 */
export interface EntityLoadOutcome {
  status: EntityLoadStatus;
  targetTable: string | null;
  /** COPY INTO's count; null for a noop or skipped load, or when the COPY never reported. */
  rowsLoaded: number | null;
  /** The extractor's manifest count for this table. */
  expectedRows: number | null;
  /** COUNT(*) of the target after the load. */
  totalRecords: number | null;
  /**
   * #2392: the source sent no row and no column for the table, so nothing was loaded (`skipped`).
   * `not_created`: the table was never created in the warehouse. `kept`: it exists from an
   * earlier import and was left as it is. Absent on every other outcome (`shared/runs/noRows.ts`).
   */
  noRows?: "not_created" | "kept";
  /**
   * Product gaps row 22: staged rows the incremental MERGE left out because a later row of the
   * same key was in the same batch. Absent when the load counted none (a full refresh, the no-op
   * client, an entity that stopped first); `0` when it counted and found none.
   */
  duplicateRowsDropped?: number;
  /**
   * Sum of this table's statement durations, in seconds (one decimal); null when none. #2404: from
   * its outcome row's `loadDurationMs`, or an older run's `LOAD_STATEMENT` rows.
   */
  durationSeconds: number | null;
  /**
   * SEC-17: the only field here derived from warehouse text, and it is exactly what the load
   * events already carry: the runner's own sentence (`LOAD_ENTITY_FAILED.error`) or the
   * `sanitiseDriverError` summary (`errorSummary`), never a driver's raw message. Null unless
   * the load failed.
   */
  error: string | null;
  /** The load step row's `attempt`: only the current attempt's metrics are ever attached. */
  attempt: number;
  /**
   * Round 6 (T-02): the SQL extractor's own diff of the table's source schema, counted, from the
   * load's `metrics.entities[].sourceSchemaChanges`. Absent when the image reported none.
   */
  sourceSchemaChanges?: SourceSchemaChanges;
}

/** A table's source schema change as the run detail line counts it (round 6, T-02). */
export interface SourceSchemaChanges {
  added: number;
  dropped: number;
  typeChanged: number;
}

/**
 * Round 6 (T-02, instance 0014): the SQL extractor v1.7.0's read summary of one table, from its
 * `_manifest.json` row (`runSummary`). Counts only (SEC-17).
 */
export interface EntityReadSummary {
  rowsRead: number;
  /** Wall-clock read seconds across every attempt; null when the image did not say. */
  readSeconds: number | null;
  /** Whole-table re-reads; the Tables view shows them only when there were any. */
  retries: number;
  /** The slices of a partitioned read; null for a read in one pass. */
  slices: number | null;
}

/**
 * #1663: the column changes a run found in one table, counted from `schema_changes` rows first
 * seen in that run (a `pending` row is not yet a change). Counts only: no column name crosses the
 * runs API (the connection's change log lists them, `dataflows.read`).
 */
export interface EntitySchemaChanges {
  /** The source sends a column the table did not have and the load added it. */
  added: number;
  /** The table has a column the source does not send. It is kept and not loaded. */
  notSent: number;
  /** The type differs from the table's. */
  typeChanged: number;
  /** A key or watermark column is not sent. */
  keyLost: number;
  /** New columns held back for a decision (Review policy) or ignored: not added to the table. */
  held: number;
  /** Changes (any kind) that stopped the table: it did not load. */
  stopped: number;
}

export interface EntityProgressRow {
  dataflowId: string;
  entityName: string;
  /** The extract's status; `waiting` for a planned table with no progress row yet (#1127). */
  status: RunEntityStatus;
  records: number;
  /** Never written after the insert (always 0); kept in the API, no longer shown. */
  pages: number;
  startedAt: string | null;
  finishedAt: string | null;
  error: string | null;
  /** The load's outcome for this table. Absent when the load has not run for it (yet). */
  load?: EntityLoadOutcome;
  /**
   * False when this row was synthesised from the load metrics because the progress drain never
   * saw a `TABLE_*` message for the table. Absent on a row the extract reported.
   */
  extractReported?: boolean;
  /** Extract seconds from `startedAt`/`finishedAt` (one decimal); absent while it runs. */
  durationSeconds?: number;
  /** Round 6 (T-02): the extractor's read summary; absent when the image wrote none. */
  read?: EntityReadSummary;
  /**
   * Product gaps row 4: a failed table's classified cause, from its step's `metrics.tableCauses`
   * (the load's when the load failed, else the extract's). Absent when none was recorded.
   */
  cause?: ImportCauseView;
  /**
   * #1663: the column changes this run found in the table. Absent when it found none (a run with
   * no change shows nothing new).
   */
  schemaChanges?: EntitySchemaChanges;
}

/** The most tables `RunFailure.tables` lists; `tableTotal` counts them all. */
export const RUN_FAILURE_TABLES_MAX = 10;

/** The longest `RunFailure.message`, the same cap as a stored cleaned error (SEC-17). */
export const RUN_FAILURE_MESSAGE_MAX = 1000;

/**
 * Round 10 (R2.1): the summary of a failed or partial run, built by the server from the step's
 * cleaned error and the existing cause tables, so the client composes no sentence. `message` is
 * the cleaned error (SEC-17 already applied), at most `RUN_FAILURE_MESSAGE_MAX` characters, with
 * a bare extractor code replaced by its label.
 */
export interface RunFailure {
  /** The failed step's label ("Extract from SQL Server"), the first failed step in chain order. */
  step: string;
  /** Up to `RUN_FAILURE_TABLES_MAX` failed tables, in name order. */
  tables: Array<{
    dataflowId: string;
    /** The connection's name; null when the step rows name none. */
    connection: string | null;
    name: string;
    label: string | null;
  }>;
  tableTotal: number;
  /** The classified cause's short label; null when the step recorded none. */
  category: string | null;
  message: string | null;
  /**
   * What to do: the cause's fix text and its action button label; null without a cause. #1084: an
   * import cause with an action also carries what the cause block reads to decide who may take it
   * (`permission`, `withoutPermission`, `action`), so the card shows the action only to a reader
   * holding the key and the ask line to the others, as the cause block does.
   */
  whatToDo: {
    fix: string;
    actionLabel: string | null;
    permission?: PermissionKey | null;
    withoutPermission?: string | null;
    action?: ImportCauseAction | null;
  } | null;
  /**
   * Round 10 wave 2 (R2.4): the run's first error event whose raw text Log Analytics may hold
   * (it carries `errorFingerprint` or `executionName`), for the card's "Technical details". An id
   * only; absent when the run has none.
   */
  technicalEventId?: string;
}

/** `GET /runs/:runId`: the stepper shape the run detail screen renders (FR-53, FR-56). */
export interface RunDetail extends RunSummaryRow {
  summary: unknown;
  /**
   * Instance 0032 (#824): the instant a scheduled run was due (`runs.scheduled_for`), ISO; null for
   * a run no schedule fired, and for a scheduled run created before the column existed. Absent on
   * older servers.
   */
  scheduledFor?: string | null;
  steps: RunStepRow[];
  entities: EntityProgressRow[];
  /** #1663: how many tables of this run found a column change. Absent when none did. */
  schemaChangedTables?: number;
  /** #1663: how many of those tables a column change stopped (they did not load). Absent when none. */
  schemaStoppedTables?: number;
  /**
   * Product gaps row 2: per connection, the tables that did not land in this run and how a table
   * retry would read each. Present only once the run is terminal and table runs are enabled in
   * this deployment (`SKYDATA_TABLE_RUNS`); a connection with nothing to retry is left out.
   */
  retryable?: RetryableConnection[];
  /**
   * #982: who cancelled a cancelled run, masked as `startedBy` is ("System" for the runner).
   * Absent for a run that was not cancelled, one with no cancel event, and older servers.
   */
  cancelledBy?: string;
  /**
   * #1291: when the run first started, ISO, once a retry has restarted it (a retry moves
   * `startedAt` to the retry, B-30). Absent for a run never retried, a retry from before this
   * field, and older servers.
   */
  firstStartedAt?: string;
  /** Round 10 (R2.1): the failure summary; null unless the run is `failed` or `partially_succeeded`. */
  failure?: RunFailure | null;
}

/** The most models, and the most tests, `GET /runs/:runId/transform-results` lists. */
export const MAX_RUN_TRANSFORM_NODES = 5_000;

/**
 * One dbt model of a run's transform step (Session 10, P11): the `DBT_MODEL` row of the step's
 * CURRENT pass (`transform.ts`), field-selected. Identifiers, enums, counts and durations only
 * (SEC-17); `cause` is the server's classified view, never dbt's text.
 */
export interface RunTransformModelResult {
  /** dbt's unique id, `model.<project>.<name>`. */
  node: string;
  /** The node's short name, for the grid. */
  name: string;
  status: DbtResultStatus | "unknown";
  failed: boolean;
  executionTimeSeconds: number | null;
  rowsAffected: number | null;
  materialization: DbtMaterialization | "other" | null;
  cause: DbtCauseView | null;
}

/** One dbt test of a run's transform step: its `DBT_TEST` row of the current pass. */
export interface RunTransformTestResult {
  node: string;
  name: string;
  status: DbtResultStatus | "unknown";
  failed: boolean;
  /** Failing rows, or values for `unique` and `accepted_values`; null when not reported. */
  failures: number | null;
  executionTimeSeconds: number | null;
  kind: DbtTestKind | null;
  column: string | null;
  /** The model the test is attached to, as a unique id. */
  model: string | null;
  cause: DbtCauseView | null;
}

/**
 * `GET /runs/:runId/transform-results` (Session 10, P11; SF7): the models and tests of the run's
 * transform step under the server's current-pass rule (`dbtJobModelHistory.ts`), failures
 * first. Empty lists for a run with no transform step. `truncated` when either list passed
 * `MAX_RUN_TRANSFORM_NODES`.
 */
export interface RunTransformResults {
  models: RunTransformModelResult[];
  tests: RunTransformTestResult[];
  truncated: boolean;
  /**
   * The transform step's primary classified cause (`run_steps.metrics.cause`, S7), built by the
   * server (`toDbtCauseView(parseDbtCause(...))`); null when the step classified nothing or the
   * run has no transform step. The run page's step details show it with `CauseBlock`.
   */
  cause: DbtCauseView | null;
  /**
   * #1083: whether this run's dbt pass asked for tests, from the selection the transform step
   * recorded when it started dbt (`metrics.testSelect`): true when one was recorded, false when
   * it recorded none (Run tests off on every dbt job of the run), null when dbt never started
   * for this pass or the row predates the field. The run page's Tests empty state reads it.
   */
  testsRequested: boolean | null;
}

export interface RunEventRow {
  id: string;
  runId: string;
  dataflowId: string | null;
  entityName: string | null;
  level: string;
  event: string;
  payload: unknown;
  at: string;
}

/**
 * Which image put a progress message on the `run-progress` queue.
 *
 * One queue carries two image families (DATA-MODEL §5 and §5b): the connectors publish the
 * `EXTRACT_*`/`TABLE_*` events, and the dbt image publishes the `TRANSFORM_*` events below. The
 * envelope is identical — the drain cannot tell them apart by shape — so the source is derived
 * from the event name and stamped on the `run_events` payload, which is what lets a reader ask
 * "what did the transform report" without matching on prefixes of its own.
 */
export type RunEventSource = "connector" | "transform";

/**
 * The events the dbt image publishes onto `run-progress` (FR-62).
 *
 * Kept here rather than in the drain because three places have to agree on the exact strings:
 * the image (`transform/skydata_dbt/progress.py`, whose constants this mirrors), the drain that
 * derives `source` from them, and the walkthrough that asserts on the rows. `TRANSFORM_*` events
 * the RUNNER writes (`TRANSFORM_START_ERROR`, `TRANSFORM_MODEL_SKIPPED`,
 * `TRANSFORM_ALREADY_RUNNING`) are not here: they never travel over the queue, and they are
 * already written with the runner as their author.
 */
export const TRANSFORM_PROGRESS_EVENTS = [
  "TRANSFORM_STARTED",
  "TRANSFORM_MODEL_STARTED",
  "TRANSFORM_MODEL_COMPLETED",
  "TRANSFORM_MODEL_FAILED",
  "TRANSFORM_TEST_COMPLETED",
  "TRANSFORM_COMPLETED",
] as const;

export type TransformProgressEvent = (typeof TRANSFORM_PROGRESS_EVENTS)[number];

/**
 * #1109: what a live frame's error reads when the web could not read the instance's loader
 * identity values in time. The frame never carries the text unread, and a failed table still
 * says something. The run is read again when its step finishes, and the stored reason then
 * replaces this sentence (`mergeEntityProgress` keeps a settled fetched row).
 */
export const LIVE_ERROR_PENDING = "The reason shows when this step finishes.";

/**
 * The live progress message the web app pushes over `/ws/pipeline-status` (FR-55).
 *
 * The runner and the web app are separate processes, so this shape also travels between them
 * as the `pg_notify` payload on `skydata_run_events`; see
 * `runner/steps/progressDrain.ts` for the contract and the reason.
 */
export interface RunProgressNotification {
  instanceId: string;
  runId: string;
  dataflowId: string | null;
  entityName: string | null;
  event: string;
  level: string;
  status: EntityProgressStatus | null;
  records: number | null;
  attempt: number | null;
  /**
   * SEC-17: never the connector's raw text. A bounded summary with quoted literals removed,
   * plus the category it was classified into; see `sanitiseConnectorError` in the drain.
   */
  error: string | null;
  errorCategory: string | null;
  /**
   * #1108: the table's classified cause (the drain's `classifyExtractText`), a closed code only,
   * so a cause-only reader's frame names the same cause as the run page. Absent on an older drain's
   * frame and on a frame with no error.
   */
  causeSource?: string | null;
  causeCode?: string | null;
  at: string;
  /**
   * Session 10 (P12): the step a `STEP_*` frame is about. The runner sends it from
   * `transitionStep` and `skipUnreachableSteps`; absent (or null) on the drain's entity frames
   * and on the load's `LOAD_*` frames. A frame with `step` or `runStatus` is relayed as
   * `type: "status"`, every other one as `entity_progress`.
   */
  step?: StepName | null;
  /** The status the step moved to, with `step`. */
  stepStatus?: StepStatus | null;
  /**
   * #2392 (review): a load's `LOAD_ENTITY_SKIPPED` frame for a table the source sent nothing for,
   * the closed outcome only (`LoadOutcome.noRows`), so a running import reads the table as the
   * record will. Absent on every other frame.
   */
  noRows?: "not_created" | "kept";
  /**
   * The run's own status when the frame changed it: `running` on `RUN_RUNNING`, and the
   * terminal status on finalize's own `STEP_SUCCEEDED`, which is the run's last frame.
   */
  runStatus?: RunStatus | null;
}
